import { useState, useCallback, useRef } from "react";
import * as Constants from "../utils/constants";

export function useClipboardSafe() {
  const [supported, setSupported] = useState(
    !!navigator.clipboard && !!navigator.permissions
  );
  const selectedElementRef = useRef();

  const readClipboard = useCallback(async () => {
    if (!supported) {
      console.log("Clipboard API not supported in this browser.");
      return null;
    }

    if (!document.hasFocus()) {
      console.log("Document not focused.");
      return null;
    }

    try {
      // Check permissions first
      const permissionStatus = await navigator.permissions.query({
        name: "clipboard-read",
      });

      if (permissionStatus.state === "denied") {
        console.log("Clipboard read permission denied.");
        return null;
      }

      // Must be triggered by a user gesture (click/key event)
      const items = await navigator.clipboard.read();

      let html, text;
      for (const item of items) {
        for (const type of item.types) {
          const blob = await item.getType(type);
          const content = await blob.text();

          if (type === "text/plain") text = content;
          if (type === "text/html") html = content;
        }
      }
      if (!html)
        html = text;

      return { text, html };
    } catch (err) {
      console.error("Clipboard read error:", err);
      return null;
    }
  }, [supported]);

  const canPaste = () => {
    if (!selectedElementRef.current)
      return;
    if (!selectedElementRef.current.elementRef)
      return false;
    if (!selectedElementRef.current.range || selectedElementRef.current.range.index < 0)
      return false;
    return true;
  }

  const pasteText = async (editorFileRef, formikValuesRef) => {
    const { text, html } = await readClipboard();
    if (!text && !html)
      return;

    if (!canPaste())
      return;

    // paste text into rich text editor
    if (selectedElementRef.current.type === Constants.editorType.text) {
      const quill = selectedElementRef.current.elementRef;
      const clipboardModule = quill.getModule("clipboard");
      clipboardModule.handlePasteContent(quill, selectedElementRef.current.range, text, html, false);
    }

    // paste text into input field
    if (selectedElementRef.current.type === Constants.editorType.inputField) {
      const editorInputField = selectedElementRef.current.elementRef;
      const inputFieldValue = formikValuesRef.current[editorInputField.Id];
      const startIndex = selectedElementRef.current.range.index;
      const endIndex = selectedElementRef.current.range.index + selectedElementRef.current.range.length;
      const newValue = inputFieldValue.slice(0, startIndex) + text + inputFieldValue.slice(endIndex);
      formikValuesRef.current[editorInputField.Id] = newValue;
      editorInputField.Value = newValue;

      if (editorInputField.VarName && editorFileRef.current.EditorInputFields) {
        let editorInputFieldWithSameVarName = editorFileRef.current.EditorInputFields.filter(x => x.VarName === editorInputField.VarName);
        if (editorInputFieldWithSameVarName) {
          editorInputFieldWithSameVarName.forEach(x => {
            formikValuesRef.current[x.Id] = newValue;
            x.Value = newValue;
          });
        }
      }
    }
  }

  const saveSelectedElement = (element) => {
    selectedElementRef.current = element;
  }

  return {
    readClipboard,
    canPaste,
    pasteText,
    saveSelectedElement,
  };
}
